import React from 'react';
import { GoogleMap, Marker, useJsApiLoader, OverlayView, Polygon } from '@react-google-maps/api';
import { useNavigate } from 'react-router-dom';
import { rentalDetailPath, roommateDetailPath } from '../../utils/listingPaths';

const containerStyle = {
  width: '100%',
  height: '100%',
};

// 自定义InfoWindow样式
const infoWindowStyle = {
  border: 'none',
  borderRadius: '8px',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
};

const DEFAULT_CENTER = { lat: 39.8283, lng: -98.5795 }; // 美国大陆地理中心（堪萨斯州）

const MapViewComponent = function MapView({
  markers = [],
  selectedId = null,
  hoveredId = null,
  centerLat,
  centerLng,
  onMarkerClick,
  onMarkerHover,
  markerData = {},
  onBoundsChanged,
  allowBoundsChange = true,
  uscPolygon = null,
  /** 为 true 时不根据 markers 自动 fitBounds（例如用户已拖地图、URL 带可视范围时） */
  suppressAutoFit = false,
  /** URL 上的可视范围（与 suppressAutoFit 配套）；组件挂载后 fit 一次，从详情返回时能还原视角 */
  initialUrlBounds = null,
}) {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: 'AIzaSyABl8lts6BmCwMfw-cVqa1Xy3xDxoEJhUg',
  });
  const [previewId, setPreviewId] = React.useState(null);
  const [hovering, setHovering] = React.useState(false);
  const [hoverTimer, setHoverTimer] = React.useState(null);
  const [closeTimer, setCloseTimer] = React.useState(null);
  const mapRef = React.useRef(null);
  const urlBoundsAppliedKeyRef = React.useRef(null);
  const [mapReady, setMapReady] = React.useState(false);
  const navigate = useNavigate();
  const allowBoundsChangeRef = React.useRef(allowBoundsChange);

  // 更新ref值
  React.useEffect(() => {
    allowBoundsChangeRef.current = allowBoundsChange;
  }, [allowBoundsChange]);

  React.useEffect(() => {
    if (!isLoaded) {
      setMapReady(false);
    }
  }, [isLoaded]);

  // 必须用稳定引用：父组件 hover/loading 每次 render 若新建 {lat,lng}，GoogleMap 会对 center 调 setCenter，地图永远被拽回搜索点，表现为拖不动
  const searchCenter = React.useMemo(() => {
    if (
      centerLat != null &&
      centerLng != null &&
      !Number.isNaN(centerLat) &&
      !Number.isNaN(centerLng)
    ) {
      return { lat: centerLat, lng: centerLng };
    }
    return null;
  }, [centerLat, centerLng]);

  // 仅用「内容」做依赖，避免父组件每次 render 新建 markers 数组就重复 fit、把地图拽回初始视野
  const markerFingerprint = React.useMemo(
    () =>
      JSON.stringify(
        markers.map((m) => [String(m.id), Number(m.lat), Number(m.lng)])
      ),
    [markers]
  );

  // 无搜索点时让地图中心落在房源附近，避免 GoogleMap 长期停在 DEFAULT_CENTER（堪萨斯）；引用随 fingerprint 稳定
  const markersCentroid = React.useMemo(() => {
    try {
      const tuples = JSON.parse(markerFingerprint);
      if (!Array.isArray(tuples) || tuples.length === 0) return null;
      let sumLat = 0;
      let sumLng = 0;
      let n = 0;
      tuples.forEach((t) => {
        const lat = t[1];
        const lng = t[2];
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          sumLat += lat;
          sumLng += lng;
          n += 1;
        }
      });
      if (n === 0) return null;
      return { lat: sumLat / n, lng: sumLng / n };
    } catch {
      return null;
    }
  }, [markerFingerprint]);

  // URL 带 ne/sw 且 suppressAutoFit 时不会走 marker fit，这里至少还原用户上次拖出的视窗（从详情页返回时常见）
  React.useEffect(() => {
    if (!isLoaded || !mapReady || !mapRef.current || !window.google?.maps) return;
    if (!suppressAutoFit || !initialUrlBounds) {
      urlBoundsAppliedKeyRef.current = null;
      return;
    }
    const { ne, sw } = initialUrlBounds;
    const nelat = Number(ne.lat);
    const nelng = Number(ne.lng);
    const swlat = Number(sw.lat);
    const swlng = Number(sw.lng);
    if (![nelat, nelng, swlat, swlng].every(Number.isFinite)) return;
    const key = `${nelat},${nelng},${swlat},${swlng}`;
    if (urlBoundsAppliedKeyRef.current === key) return;
    urlBoundsAppliedKeyRef.current = key;
    const bounds = new window.google.maps.LatLngBounds();
    bounds.extend({ lat: nelat, lng: nelng });
    bounds.extend({ lat: swlat, lng: swlng });
    mapRef.current.fitBounds(bounds, 50);
  }, [isLoaded, mapReady, suppressAutoFit, initialUrlBounds]);

  // 有搜索中心：随 marker 集合变化反复 fit「房源 + 搜索点」（避免首帧 markers 为空时 fit 退化 bounds 后永不更新导致灰屏）
  // 无搜索中心：仅在 marker 集合实际变化时 fit；suppressAutoFit 时两者都跳过以保留用户拖动后的视野
  React.useEffect(() => {
    if (!isLoaded || !mapReady || !mapRef.current || !window.google?.maps) return;

    const map = mapRef.current;

    if (
      centerLat != null &&
      centerLng != null &&
      !Number.isNaN(centerLat) &&
      !Number.isNaN(centerLng) &&
      !suppressAutoFit
    ) {
      const bounds = new window.google.maps.LatLngBounds();
      markers.forEach((marker) => {
        bounds.extend({ lat: marker.lat, lng: marker.lng });
      });
      bounds.extend({ lat: centerLat, lng: centerLng });

      if (markers.length === 0) {
        map.setCenter({ lat: centerLat, lng: centerLng });
        map.setZoom(13);
        return;
      }

      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();
      if (ne.equals(sw)) {
        map.setCenter({ lat: centerLat, lng: centerLng });
        map.setZoom(14);
      } else {
        map.fitBounds(bounds, 50);
      }
      return;
    }

    if (!suppressAutoFit && markers.length >= 1) {
      const bounds = new window.google.maps.LatLngBounds();
      markers.forEach((marker) => {
        bounds.extend({ lat: marker.lat, lng: marker.lng });
      });
      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();
      if (ne.equals(sw)) {
        map.setCenter({ lat: markers[0].lat, lng: markers[0].lng });
        map.setZoom(14);
      } else {
        map.fitBounds(bounds, 50);
      }
    }
    // 仅 markerFingerprint 表示 marker 集合是否变化；不把 markers 引用放进依赖，避免 hover 等重渲染反复 fit
  }, [isLoaded, mapReady, centerLat, centerLng, markerFingerprint, suppressAutoFit]);

  const center = React.useMemo(
    () => searchCenter || markersCentroid || DEFAULT_CENTER,
    [searchCenter, markersCentroid]
  );

  // 稳定引用，避免 GoogleMap 每次 setOptions 把用户缩放拽回默认值
  const uscPolygonOptions = React.useMemo(
    () => ({
      fillColor: '#990000',
      fillOpacity: 0.12,
      strokeColor: '#990000',
      strokeOpacity: 0.95,
      strokeWeight: 2,
      clickable: false,
      zIndex: 0,
    }),
    []
  );

  const mapOptions = React.useMemo(
    () => ({
      zoom: 11,
      mapTypeControl: true,
      mapTypeControlOptions: {
        style: 0, // DEFAULT
        mapTypeIds: ['roadmap', 'satellite'],
        position: 1, // TOP_LEFT
      },
      zoomControl: true,
      streetViewControl: false,
      fullscreenControl: false,
      scaleControl: false,
      rotateControl: false,
      clickableIcons: false,
      minZoom: 2,
      draggable: true,
    }),
    []
  );

  // 仅在用户拖动结束时同步 URL（不要用 zoom_changed：地图初始化 / fitBounds 也会触发，会把错误区域写进路由）
  const handleMapLoad = map => {
    mapRef.current = map;
    setMapReady(true);

    map.addListener('dragend', () => {
      if (allowBoundsChangeRef.current && typeof onBoundsChanged === 'function') {
        const bounds = map.getBounds();
        if (bounds) {
          const ne = bounds.getNorthEast();
          const sw = bounds.getSouthWest();
          onBoundsChanged({
            ne: { lat: ne.lat(), lng: ne.lng() },
            sw: { lat: sw.lat(), lng: sw.lng() },
            center: { lat: map.getCenter().lat(), lng: map.getCenter().lng() },
            zoom: map.getZoom(),
          });
        }
      }
    });
  };

  // 悬停marker 1秒后显示预览
  const handleMarkerMouseEnter = marker => {
    setPreviewId(marker.id);
    onMarkerHover && onMarkerHover(marker);
  };
  const handleMarkerMouseLeave = marker => {
    setPreviewId(null);
    onMarkerHover && onMarkerHover(null);
  };
  const handleCardMouseEnter = marker => {
    setHovering(true);
    if (closeTimer) clearTimeout(closeTimer);
  };
  const handleCardMouseLeave = marker => {
    setHovering(false);
    setCloseTimer(setTimeout(() => {
      if (!hovering) {
        setPreviewId(null);
        onMarkerHover && onMarkerHover(null);
      }
    }, 200));
  };

  if (!isLoaded) return <div>Loading Map...</div>;

  // 瘦长Material风格SVG marker
  const svgMaterialRed = 'data:image/svg+xml;utf8,<svg width="40" height="56" viewBox="0 0 40 56" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M20 2C11.16 2 4 9.16 4 18c0 9.84 8.16 22.08 14.12 30.04a2.5 2.5 0 0 0 3.76 0C27.84 40.08 36 27.84 36 18c0-8.84-7.16-16-16-16zm0 22a6 6 0 1 1 0-12 6 6 0 0 1 0 12z" fill="%23E53935"/></svg>';
  const svgMaterialBlue = 'data:image/svg+xml;utf8,<svg width="40" height="56" viewBox="0 0 40 56" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M20 2C11.16 2 4 9.16 4 18c0 9.84 8.16 22.08 14.12 30.04a2.5 2.5 0 0 0 3.76 0C27.84 40.08 36 27.84 36 18c0-8.84-7.16-16-16-16zm0 22a6 6 0 1 1 0-12 6 6 0 0 1 0 12z" fill="%231E88E5"/></svg>';
  const svgMaterialOrange = 'data:image/svg+xml;utf8,<svg width="40" height="56" viewBox="0 0 40 56" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M20 2C11.16 2 4 9.16 4 18c0 9.84 8.16 22.08 14.12 30.04a2.5 2.5 0 0 0 3.76 0C27.84 40.08 36 27.84 36 18c0-8.84-7.16-16-16-16zm0 22a6 6 0 1 1 0-12 6 6 0 0 1 0 12z" fill="%23FF9800"/></svg>';

  return (
    <GoogleMap
      mapContainerStyle={containerStyle}
      center={center}
      options={mapOptions}
      onLoad={handleMapLoad}
      onMouseLeave={() => {
        setPreviewId(null);
        onMarkerHover && onMarkerHover(null);
      }}
    >
      {Array.isArray(uscPolygon) && uscPolygon.length >= 3 && (
        <Polygon paths={uscPolygon} options={uscPolygonOptions} />
      )}
      {/* 搜索中心点marker（大红色）：若与房源不重合才渲染纯红色静态pin */}
      {searchCenter && !markers.some(
        m => Math.abs(m.lat - searchCenter.lat) < 0.0001 && Math.abs(m.lng - searchCenter.lng) < 0.0001
      ) && (
        <Marker
          position={searchCenter}
          icon={{ url: svgMaterialRed, scaledSize: { width: 40, height: 56 } }}
          zIndex={999}
        />
      )}
      {/* 房源marker和自定义预览卡片 */}
      {markers.map(marker => (
        <React.Fragment key={marker.id}>
          <Marker
            position={{ lat: marker.lat, lng: marker.lng }}
            onClick={() => {
              const item = markerData[marker.id];
              if (typeof window !== 'undefined' && window.location.pathname.startsWith('/roommates')) {
                navigate(roommateDetailPath(item || marker.id));
              } else {
                navigate(rentalDetailPath(item || marker.id));
              }
            }}
            onMouseOver={() => handleMarkerMouseEnter(marker)}
            onMouseOut={() => handleMarkerMouseLeave(marker)}
            icon={(() => {
              const isActive = selectedId === marker.id || hoveredId === marker.id || previewId === marker.id;
              const isSearchCenter = searchCenter &&
                Math.abs(marker.lat - searchCenter.lat) < 0.0001 &&
                Math.abs(marker.lng - searchCenter.lng) < 0.0001;
              if (isActive) return { url: svgMaterialBlue, scaledSize: { width: 40, height: 56 } };
              if (isSearchCenter) return { url: svgMaterialRed, scaledSize: { width: 40, height: 56 } };
              return { url: svgMaterialOrange, scaledSize: { width: 40, height: 56 } };
            })()}
          />
          {previewId === marker.id && markerData[marker.id] && (
            <OverlayView
              position={{ lat: marker.lat, lng: marker.lng }}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
              getPixelPositionOffset={() => ({ x: -120, y: -180 })}
            >
              <div
                style={{
                  width: 240,
                  background: 'white',
                  borderRadius: 16,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
                  cursor: 'pointer',
                  padding: 0,
                  border: 'none',
                  transition: 'box-shadow 0.2s',
                  zIndex: 1000,
                }}
                onClick={() => {
                  const item = markerData[marker.id];
                  if (typeof window !== 'undefined' && window.location.pathname.startsWith('/roommates')) {
                    navigate(roommateDetailPath(item || marker.id));
                  } else {
                    navigate(rentalDetailPath(item || marker.id));
                  }
                }}
                onMouseEnter={() => setPreviewId(marker.id)}
                onMouseLeave={() => setPreviewId(null)}
              >
                <img
                  src={markerData[marker.id].images?.[0] || 'https://source.unsplash.com/random/320x180/?apartment'}
                  alt={markerData[marker.id].title}
                  style={{
                    width: '100%',
                    height: 120,
                    objectFit: 'cover',
                    borderRadius: '16px 16px 0 0',
                    display: 'block',
                  }}
                />
                <div style={{ fontWeight: 600, padding: '10px 16px', fontSize: 16 }}>
                  {markerData[marker.id].title}
                </div>
              </div>
            </OverlayView>
          )}
        </React.Fragment>
      ))}
    </GoogleMap>
  );
};

export default React.memo(MapViewComponent, (prevProps, nextProps) => {
  return (
    prevProps.centerLat === nextProps.centerLat &&
    prevProps.centerLng === nextProps.centerLng &&
    JSON.stringify(prevProps.markers) === JSON.stringify(nextProps.markers) &&
    JSON.stringify(prevProps.markerData) === JSON.stringify(nextProps.markerData) &&
    prevProps.allowBoundsChange === nextProps.allowBoundsChange &&
    prevProps.suppressAutoFit === nextProps.suppressAutoFit &&
    JSON.stringify(prevProps.initialUrlBounds) === JSON.stringify(nextProps.initialUrlBounds) &&
    prevProps.selectedId === nextProps.selectedId &&
    prevProps.hoveredId === nextProps.hoveredId &&
    JSON.stringify(prevProps.uscPolygon) === JSON.stringify(nextProps.uscPolygon)
  );
}); 